<template>
  <div class="mb-4 p-3 bg-yellow-100 text-yellow-900 rounded border border-yellow-300">
    ⚠️ Pour garantir le bon fonctionnement du lien de <b>Provenance</b>, il est recommandé d'importer les <b>Locations</b> avant les <b>MaterialEntities</b>.
  </div>
  <AdminAssetTable
    :config="materialEntitiesConfig"
    :items="materialEntitiesData"
    :users="users"
    :api-service="materialEntitiesApiService"
    @refresh="loadMaterialEntities"
    @created="handleMaterialEntityCreated"
    @updated="handleMaterialEntityUpdated"
    @deleted="handleMaterialEntityDeleted"
  />
</template>

<script setup lang="ts">
import { materialEntities } from '@/api/materialEntities';
import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import type { MaterialEntity } from '@/api/types/entities/materialEntity/types';
import { fetchAllUsers } from '@/api/users';
import AdminAssetTable from '@/components/admin/AdminAssetTable.vue';
import { ADMIN_ASSET_CONFIGS } from '@/configs/adminAssetConfigs';
import { onMounted, ref } from 'vue';

// Configuration
const materialEntitiesConfig = ADMIN_ASSET_CONFIGS.materialEntities;

// État
const materialEntitiesData = ref<MaterialEntity[]>([]);
const users = ref<User[]>([]);
const loading = ref(false);

// Service API adapté au format attendu par AdminAssetTable
const materialEntitiesApiService = {
  async getAll(): Promise<MaterialEntity[]> {
    return await materialEntities.getAllAdminWithDeleted();
  },
  
  async create(data: Record<string, unknown>): Promise<MaterialEntity> {
    return await materialEntities.createMaterialEntity(data as any);
  },
  
  async update(uuid: string, data: Record<string, unknown>): Promise<MaterialEntity> {
    return await materialEntities.updateMaterialEntity(uuid, data as any);
  },
  
  async delete(uuid: string): Promise<void> {
    return await materialEntities.deleteMaterialEntity(uuid);
  },
  async hardDelete(uuid: string): Promise<void> {
    await materialEntities.hardDeleteMaterialEntity(uuid);
  },
  async restore(uuid: string): Promise<void> {
    await materialEntities.restoreMaterialEntity(uuid);
  }
};

// Méthodes
const loadMaterialEntities = async (): Promise<void> => {
  loading.value = true;
  try {
    materialEntitiesData.value = await materialEntitiesApiService.getAll();
  } catch (error) {
    console.error('Erreur lors du chargement des entités matérielles:', error);
  } finally {
    loading.value = false;
  }
};

const loadUsers = async (): Promise<void> => {
  try {
    users.value = await fetchAllUsers();
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
  }
};

// Gestionnaires d'événements
const handleMaterialEntityCreated = (materialEntity: Entity): void => {
  materialEntitiesData.value.push(materialEntity as MaterialEntity);
};

const handleMaterialEntityUpdated = (updatedMaterialEntity: Entity): void => {
  const index = materialEntitiesData.value.findIndex((a: MaterialEntity): boolean => a.uuid === updatedMaterialEntity.uuid);
  if (index !== -1) {
    materialEntitiesData.value[index] = updatedMaterialEntity as MaterialEntity;
  }
};

const handleMaterialEntityDeleted = (uuid: string): void => {
  materialEntitiesData.value = materialEntitiesData.value.filter((a: MaterialEntity): boolean => a.uuid !== uuid);
};

// Lifecycle
onMounted(async (): Promise<void> => {
  await Promise.all([
    loadMaterialEntities(),
    loadUsers()
  ]);
});
</script> 